"use client";

import { AnimatedDigit } from "./AnimatedDigit";
import { CONTENT_FRAME } from "@/shared/components/BlockWrapper";
import type { BlockSectionBg, CommonBlockProps } from "./common/types";
import { displayFontClassName } from "@/lib/fonts";
import { cn } from "@/lib/utils";
import {
  getSectionGridCellClass,
  getSectionGridShellClass,
  getTintedSectionScopeClass,
} from "./blockSection";

interface DigitItem {
  digit: string;
  text: string;
}

interface DigitsBlockProps extends CommonBlockProps {
  digits: DigitItem[];
}

type DigitsTheme = {
  section: string;
  digit: string;
  label: string;
};

/** Figma block_digits: lite, grey (red), dark — разделители и цвет текста по теме. */
const digitsThemeMap: Record<BlockSectionBg, DigitsTheme> = {
  light: {
    section: "bg-background",
    digit:
      "font-sans text-factoid-sm font-bold leading-[1.2] text-foreground md:text-factoid",
    label: cn(
      displayFontClassName,
      "text-heading-3-mobile leading-[1.2] text-foreground md:font-sans md:text-base md:font-bold"
    ),
  },
  default: {
    section: "bg-background",
    digit:
      "font-sans text-factoid-sm font-bold leading-[1.2] text-foreground md:text-factoid",
    label: cn(
      displayFontClassName,
      "text-heading-3-mobile leading-[1.2] text-foreground md:font-sans md:text-base md:font-bold"
    ),
  },
  muted: {
    section: "bg-muted",
    digit:
      "font-sans text-factoid-sm font-bold leading-[1.2] text-foreground md:text-factoid",
    label: cn(
      displayFontClassName,
      "text-heading-3-mobile leading-[1.2] text-foreground md:font-sans md:text-base md:font-bold"
    ),
  },
  red: {
    section: "bg-primary",
    digit:
      "font-sans text-factoid-sm font-bold leading-[1.2] text-primary-foreground md:text-factoid",
    label: cn(
      displayFontClassName,
      "text-heading-3-mobile leading-[1.2] text-primary-foreground md:font-sans md:text-base md:font-bold"
    ),
  },
  dark: {
    section: "bg-secondary",
    digit:
      "font-sans text-factoid-sm font-bold leading-[1.2] text-secondary-foreground md:text-factoid",
    label: cn(
      displayFontClassName,
      "text-heading-3-mobile leading-[1.2] text-secondary-foreground md:font-sans md:text-base md:font-bold"
    ),
  },
};

function resolveDigitsTheme(sectionBg: BlockSectionBg): DigitsTheme {
  return digitsThemeMap[sectionBg] ?? digitsThemeMap.light;
}

function getDigitsColumnsClass(count: number): string {
  if (count <= 1) return "md:grid-cols-1";
  if (count === 2) return "md:grid-cols-2";
  if (count === 3) return "md:grid-cols-3";
  return "md:grid-cols-4";
}

const digitCellPaddingClass =
  "px-4 py-6 md:px-6 md:py-6 desktop:px-10 desktop:py-12";

export function DigitsBlock({ digits, sectionBg = "light" }: DigitsBlockProps) {
  if (!digits || digits.length === 0) return null;

  const theme = resolveDigitsTheme(sectionBg);
  const gridCellClass = getSectionGridCellClass(sectionBg);
  const columns = Math.min(digits.length, 4);

  return (
    <div className={cn("w-full", theme.section, getTintedSectionScopeClass(sectionBg))}>
      <div className={CONTENT_FRAME}>
        <div
          className={cn(
            getSectionGridShellClass(sectionBg),
            "grid-cols-1",
            getDigitsColumnsClass(columns)
          )}
        >
          {digits.map((item, index) => (
            <div
              key={`${item.digit}-${index}`}
              className={cn(gridCellClass, digitCellPaddingClass)}
            >
              <div className="flex w-full flex-col gap-2 text-center">
                <AnimatedDigit digit={item.digit} className={theme.digit} />
                {item.text && (
                  <span className={theme.label}>{item.text}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
